import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@mui/styles'
import { useSelector, useDispatch } from 'react-redux'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import IconCheck from 'assets/images/icons/check.svg'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import { submitCompanyData, fetchCompanyData } from '@/redux/ducks/settings/company'
import config from '@/config'
import lang from '@/lang'
import { Title, SubTitle, TextBlock } from '@/modules/base/components/texts'
import { SelectInput, TextInput } from '@/modules/base/components/inputs'
import VatNumberInput from '@/modules/customer/components/edit/forms/information/vat-number-input'
import request from '@/modules/base/util/request'
import HintIcon from '@assets/images/icons/overdue.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import VatReturnInput from '@/modules/settings/components/tax-settings/components/vatReturnSelectInput'
import ProfitDerminationInput from '@/modules/settings/components/tax-settings/components/profitDerminationSelectInput'
import useClientSetting from '@/modules/client/_helpers/useClientSetting'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import { USER_RIGHTS, ROLES } from '@/modules/base/util/constants'

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    padding: '10px 0px',
    gap: 25,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 20,
    },
  },
  taxOffice: {
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.smedium,
    marginBottom: 30,
  },
  selectStateRow: {
    display: 'flex',
    padding: '10px 0px',
    gap: 25,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 20,
    },
  },
  inputState: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  inputOffice: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  vatNumberInput: {
    width: '100%',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 30,
  },
  addresse: {
    '& p': {
      margin: 0,
    },
    paddingTop: '20px',
  },
  addresseTitle: {
    fontWeight: 'bold',
    paddingBottom: '10px',
  },
  addressWapper: {
    display: 'flex',
    '& > *:first-child': {
      paddingRight: 25,
    },
    '& > *:last-child': {
      paddingLeft: 25,
    },
    '& > *': {
      width: '100%',
    },
  },
  hint: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    padding: 10,
    borderWidth: 1,
    borderRadius: 4,
    color: theme.black,
    margin: '10px 0',
    backgroundColor: 'rgba(50, 100, 255, 0.15)',
  },
  hintIcon: {
    verticalAlign: 'text-top',
    marginRight: 15,
    paddingLeft: 15,
    fontSize: 12,
  },
  selectInput: {
    display: 'flex',
    flexDirection: 'row',
    paddingTop: 10,
    gap: 25,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 20,
    },
  },
}))

const validationSchema = Yup.object({
  taxNumber: Yup.string().trim().min(9, lang.taxNumberInvalidError).matches('^[0-9/-]+$', lang.taxNumberInvalidError),
  wIdNr: Yup.string().trim().matches(/^DE\d{9}(-\d{5})?$/, {
    message: lang.wIdNrInvalidError,
    excludeEmptyString: true,
  }),
})

const taxOfficeUrl = `${config.resourceHost}taxoffice`
const initTaxOfficeState = { contactList: [], addresseList: [] }

const TaxInfo = props => {
  const classes = useStyles()
  const theme = useTheme()

  const { onSetScrollLock = undefined } = props

  const dispatch = useDispatch()
  const companyData = useSelector(store => store.settings.company)
  const { isLoading, taxOfficeId, zipCode } = companyData

  const { client, isClientSetting } = useClientSetting()
  const userInfo = useSelector(state => state.user.info)
  const [taxOffice, setTaxOffice] = useState(initTaxOfficeState)
  const [allStates, setAllStates] = useState({})
  const [taxOfficeState, setTaxOfficeState] = useState(null)

  const isTaxConsultant = userInfo?.roles?.includes(ROLES.TAX_CONSULTANT_ROLE)
  const readOnly = isClientSetting ? false : isTaxConsultant || !useCheckAccessRights(USER_RIGHTS.SETTINGS.WRITE)
  const [taxNumberOfficeinValid, setTaxNumberOfficeinValid] = useState(false)

  const getStateSelectOptions = () => {
    const stateOptions = Object.keys(allStates).sort().map(item => { return { value: item, label: item } })
    return {
      label: lang.tax.federalState,
      name: 'states',
      clearable: false,
      allowCreate: false,
      notAsync: true,
      customSelectOptions: {
        valueKey: 'value',
        labelKey: 'label',
        defaultOptions: true,
      },
      options: stateOptions,
      value: taxOfficeState,
      handleChange: onStateChange,
      onFocus: () => { onSetScrollLock?.(true) },
      onBlur: () => { onSetScrollLock?.(false) },
      readOnly,
      useFormik: false,
    }
  }

  const handleSubmit = useCallback((values, { setSubmitting }) => {
    dispatch(submitCompanyData({ ...values }, true, client?.id))
    setSubmitting(false)
  }, [submitCompanyData])

  const loadAllStatesWithTaxOffices = () => {
    request(`${taxOfficeUrl}/states`, { auth: true, isConcurrent: true })
      .then(({ body: { data } }) => setAllStates(data))
      .done()
  }

  useEffect(() => {
    dispatch(fetchCompanyData(client?.id))
    loadAllStatesWithTaxOffices()
  }, [])

  const validateTaxSettings = async(values, fieldName, value) => {
    // taxOffice, taxOfficeState, taxNumber, taxOfficeId are readOnly in client view
    const { taxOfficeId, taxNumber } = { ...values, [`${fieldName}`]: value }
    const url = `${config.resourceHost}vatReturn/validateTaxSetting`
    const headers = {}
    if (client?.id) headers['collaboration-id'] = client?.id
    if (taxOfficeId && taxNumber) {
      return new Promise((resolve, reject) => {
        request(url, {
          auth: true,
          isConcurrent: true,
          method: 'POST',
          data: { bufa: taxOfficeId, taxNumber },
          headers,
        })
          .then(({ body: { data: { isValid } } }) => {
            setTaxNumberOfficeinValid(!isValid)
            resolve(isValid)
          })
          .catch(() => {
            resolve(false)
          })
          .done()
      })
    } else {
      setTaxNumberOfficeinValid(false)
      return Promise.resolve(false)
    }
  }

  const onStateChange = (option) => {
    setTaxOfficeState(option.value)
  }

  useEffect(() => {
    const states = Object.values(allStates)
    if (states.length > 0) {
      if (taxOfficeId) {
        if (taxOffice.buFaNr === taxOfficeId) return
        const foundTaxOffice = states.flat().find(taxOfficeItem => taxOfficeItem.buFaNr === taxOfficeId)
        if (!foundTaxOffice) return
        changeTaxOfficeState(foundTaxOffice)
        setTaxOffice(foundTaxOffice)
      } else if (zipCode) {
        request(`${taxOfficeUrl}/${zipCode}`, { auth: true, isConcurrent: true })
          .then(({ body: { data } }) => changeTaxOfficeState(data))
          .done()
      }
    }
  }, [taxOfficeId, allStates, zipCode])

  const changeTaxOfficeState = (newTaxOffice) => {
    onStateChange({ value: newTaxOffice.state })
    validateTaxSettings(companyData)
  }

  const findOfficeState = bufaNr => {
    const states = Object.values(allStates)
    return states.flat().find(taxOfficeItem => taxOfficeItem.buFaNr === bufaNr)
  }

  const handleTaxOfficeChange = (option, name, values) => {
    const foundTaxOffice = findOfficeState(option.value)
    setTaxOffice(foundTaxOffice || initTaxOfficeState)
    validateTaxSettings(values, name, option.value)
  }

  const onBlurTaxNumber = async(name, value, values, setFieldValue) => {
    if (taxOffice.name) {
      return validateTaxSettings(values, name, value)
    }
    if (taxOfficeState) {
      const shortValue = value.substr(0, 2)
      const longValue = value.substr(0, 3)
      const taxOfficesFromState = allStates[taxOfficeState]
      const buFaNr = taxOfficesFromState.find(state => state.buFaNr.endsWith(longValue)).buFaNr ?? taxOfficesFromState.find(state => state.buFaNr.endsWith(shortValue)).buFaNr
      if (buFaNr) {
        setFieldValue('taxOfficeId', buFaNr)
        validateTaxSettings({ ...values, taxOfficeId: buFaNr }, name, value)
      }
    }
  }

  const taxOfficeOptions = useMemo(() => {
    const taxOfficesFromState = allStates[taxOfficeState] || []
    return taxOfficesFromState.map(taxOffice => { return { label: `${taxOffice.name} ${taxOffice.buFaNr}`, value: taxOffice.buFaNr } })
  }, [taxOfficeState, allStates])

  const addresseListItems = taxOffice.addresseList.map((addresse, index) => {
    const { type, street, houseNumber, zipCode, city, poBoxNumber } = addresse

    if (!type) return null

    const isHouseAddress = type === 'HausanschriftType'
    const addresseTitle = isHouseAddress ? lang.homeAddressLabel : lang.poBoxLabel
    const streetText = isHouseAddress ? `${street} ${houseNumber}` : `${lang.poBoxLabel} ${poBoxNumber}`
    const cityZipText = `${zipCode} ${city}`

    return (
      <li key={`adlitem${index}`} className={classes.addresse}>
        <p className={classes.addresseTitle}>{addresseTitle}</p>
        <p>{streetText}</p>
        <p>{cityZipText}</p>
      </li>
    )
  })

  const contactListItems = taxOffice.contactList.map((contact, index) => {
    const { label, value } = contact

    let valueElement = value

    if (label === 'Mail') {
      valueElement = <a href={`mailto:${value}`}>{value}</a>
    } else if (label === 'URL' || label === 'Kontakt2') {
      valueElement = <a href={/https?:\/\//.test(value) ? value : `https://${value}`} target="_blank">{value}</a>
    }

    return (
      <li key={index}>{label}: {valueElement}</li>
    )
  })

  return (
    <>
      <TextBlock>{lang.tax.subHeadline}</TextBlock>
      <Formik
        initialValues={companyData}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <div className={classes.wrapper}>
              <div className={classes.row}>
                <TextInput label={lang.taxNumberLabel} name='taxNumber' useFormik readOnly={readOnly}
                  onBlur={(value, name) => onBlurTaxNumber(name, value, values, setFieldValue)} />
                <div className={classes.vatNumberInput}>
                  <VatNumberInput showValidationButton={false} customClass={classes.vatNumberInput} useFormik readOnly={readOnly} client={props?.client} />
                </div>
              </div>
              <div className={classes.row}>
                <TextInput
                  label={lang.wIdNrLabel}
                  name='wIdNr'
                  useFormik
                  readOnly={readOnly}
                  labelTooltip={lang.wIdNrTooltip}
                />
                <div className={classes.vatNumberInput} />
              </div>
            </div>
            <div className={classes.selectStateRow}>
              <div className={classes.inputState}>
                <SelectInput
                  { ...getStateSelectOptions()}
                />
              </div>
              <div className={classes.inputOffice}>
                <SelectInput
                  allowCreate={false}
                  name='taxOfficeId'
                  label={lang.tax.taxOffice}
                  notAsync={true}
                  onFocus={() => { onSetScrollLock?.(true) }}
                  onBlur={() => { onSetScrollLock?.(false) }}
                  customSelectOptions={{ defaultOptions: true }}
                  options={taxOfficeOptions}
                  handleChange={(option, name) => handleTaxOfficeChange(option, name, values)}
                  readOnly={readOnly}
                  useFormik
                />
              </div>
            </div>
            {taxNumberOfficeinValid && (
              <div key='' className={classes.hint}>
                <SVGIcon svg={HintIcon} className={classes.hintIcon} height='15px' width='15px' fill={theme.primaryLight}></SVGIcon>
                <span>{lang.tax.taxNumberOfficeInvalidHint}</span>
              </div>
            )}
            <div className={classes.selectInput}>
              <VatReturnInput readOnly={readOnly} name='vatReturnPeriods' />
              <ProfitDerminationInput readOnly={readOnly} name='profitDerminationNames' />
            </div>
            <div className={classes.buttonRow}>
              <ButtonIconSquare
                label={lang.saveButtonLabel}
                icon={IconCheck}
                type='submit'
                disabled={readOnly || isLoading || isSubmitting || taxNumberOfficeinValid}
                dataQsId='settings-mycompany-tax-taxSettings-save'
              />
            </div>
            {taxOffice.name && (<>
              <Title className={classes.taxOffice} size='small'>{lang.tax.taxOffice} {taxOffice.name}</Title>
              <SubTitle >{lang.tax.taxOfficeNumber} {taxOffice.buFaNr}</SubTitle>
              <div className={classes.addressWapper}>
                <ul>
                  {addresseListItems}
                </ul>
                {contactListItems.length > 0 && <div className={classes.addresse}>
                  <p className={classes.addresseTitle}>{lang.tax.contact}</p>
                  <ul>
                    {contactListItems}
                  </ul>
                </div>
                }
              </div>
            </>
            )}
          </Form>
        ) }
      </Formik>
    </>
  )
}

TaxInfo.propTypes = {
  onSetScrollLock: PropTypes.func,
}

export default TaxInfo
